export type AuthorizedBatchResult =
  | { status: 'empty' }
  | { status: 'denied' }
  | { status: 'failed'; error: unknown }
  | { status: 'success'; ids: string[] };

type AuthorizedBatchOptions = {
  ids: readonly string[];
  authorize: (id: string) => Promise<boolean>;
  apply: (ids: string[]) => void | Promise<void>;
};

/**
 * Authorize every selected document before producing one local metadata update.
 * A denied or failed check intentionally leaves the whole selection unchanged.
 */
export async function runAuthorizedDocBatch({
  ids,
  authorize,
  apply,
}: AuthorizedBatchOptions): Promise<AuthorizedBatchResult> {
  const uniqueIds = [...new Set(ids)];
  if (uniqueIds.length === 0) {
    return { status: 'empty' };
  }

  let allowed: boolean[];
  try {
    allowed = await Promise.all(uniqueIds.map(id => authorize(id)));
  } catch (error) {
    return { status: 'failed', error };
  }

  if (allowed.some(canApply => !canApply)) {
    return { status: 'denied' };
  }

  try {
    await apply(uniqueIds);
    return { status: 'success', ids: uniqueIds };
  } catch (error) {
    return { status: 'failed', error };
  }
}
